import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PaginationControls = ({ currentPage, totalPages, totalCount, pageSize, onPageChange, loading = false, itemLabel = 'registros', onDeleteAll, hasActiveFilters = false, onDeleteFiltered }) => {
  if (totalPages <= 1) return null;

  // Calcular quais páginas mostrar
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5; // Máximo de números de páginas visíveis
    
    if (totalPages <= maxVisible) {
      // Se há poucas páginas, mostrar todas
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Lógica para mostrar páginas com elipses
      if (currentPage <= 3) {
        // Mostrar primeiras páginas
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Mostrar últimas páginas
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Mostrar páginas ao redor da atual
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);
  const singularLabel = itemLabel === 'vendas' ? 'venda' : itemLabel === 'recebimentos' ? 'recebimento' : 'registro';
  const pluralLabel = itemLabel;

  return (
    <Card className="glass-effect border-white/20 mb-4">
      <CardContent className="p-4 flex items-center justify-between flex-wrap gap-4">
        <div className="text-white/70 text-sm">
          Mostrando {startItem} a {endItem} de {totalCount} {totalCount === 1 ? singularLabel : pluralLabel}
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onPageChange(1)} 
            disabled={currentPage === 1 || loading}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onPageChange(currentPage - 1)} 
            disabled={currentPage === 1 || loading}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            Anterior
          </Button>

          {getPageNumbers().map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className="text-white/50 px-2">
                  ...
                </span>
              );
            }
            
            return (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(page)}
                disabled={loading}
                className={
                  currentPage === page
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-white/10 border-white/20 text-white hover:bg-white/20"
                }
              >
                {page}
              </Button>
            );
          })}

          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onPageChange(currentPage + 1)} 
            disabled={currentPage === totalPages || loading}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            Próxima
          </Button>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onPageChange(totalPages)} 
            disabled={currentPage === totalPages || loading}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>

          {onDeleteAll && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="ml-4 gap-2">
                  <Trash2 className="w-4 h-4" />
                  {hasActiveFilters ? `Deletar Filtrados (${totalCount})` : "Deletar Todos"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0b1727] border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Você tem certeza absoluta?</AlertDialogTitle>
                  <AlertDialogDescription className="text-white/70">
                    {hasActiveFilters 
                      ? `Isso apagará permanentemente apenas os ${totalCount} ${pluralLabel} que correspondem aos filtros atuais. Esta ação não pode ser desfeita.` 
                      : `Isso apagará permanentemente TODOS os ${pluralLabel} do seu banco de dados. Esta ação não pode ser desfeita.`}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-white/10 text-white hover:bg-white/20 border-white/20">
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={hasActiveFilters && onDeleteFiltered ? onDeleteFiltered : onDeleteAll}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {hasActiveFilters ? "Sim, Deletar Filtrados" : "Sim, Deletar Todos"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PaginationControls;

